import React, { useEffect, useState } from "react";
import { useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { ApplicationScreen, Consult } from "../../models";
import { apiAccessRequest } from "../../constants";
import CircularProgress from "@mui/material/CircularProgress";
import { BuildUrl, calculateWaitTime } from "../../helpers";
import Dialog from "@mui/material/Dialog";
import { useCreateAuditLog } from "../../hooks";

export enum UpdatePriority {
	Prioritise,
	Deprioritise
}

interface Props {
	open: boolean;
	consult?: Consult;
	action: UpdatePriority;
	close: (updated: boolean) => void;
}
export const UpdatePriorityModal = React.memo(
	({ open, consult, action, close }: Props) => {
		const { account } = useUserContext();
		const { instance } = useMsal();
		const [isError, setIsError] = useState(false);
		const [isLoading, setIsLoading] = useState(false);
		const [label, setLabel] = useState("");
		const { createAuditLog } = useCreateAuditLog();

		useEffect(() => {
			setLabel(
				action === UpdatePriority.Prioritise ? "Prioritise" : "De-prioritise"
			);
		}, [action]);

		const handleConfirmClick = async () => {
			setIsError(false);
			setIsLoading(true);
			try {
				const requestUrl = BuildUrl(
					`Consult/${consult?.id}/${
						action === UpdatePriority.Prioritise ? "Prioritise" : "Deprioritise"
					}`
				);

				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					...(tokenResponse && {
						Authorization: `Bearer ${tokenResponse.accessToken}`
					})
				};
				const response = await fetch(requestUrl, {
					method: "POST",
					headers
				});

				if (!response.ok) {
					setIsError(true);
					return;
				}
				reset(true);
				await callCreateAuditLog();
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				setIsError(true);
			} finally {
				setIsLoading(false);
			}
		};

		const reset = (updated: boolean) => {
			setIsError(false);
			close(updated);
		};

		const callCreateAuditLog = async () => {
			try {
				await createAuditLog([
					{
						patientId: consult?.patientId,
						patientName: `${consult?.firstName} ${consult?.lastName}`.trim(),
						clinicId: consult?.clinicId,
						username: account?.name ?? "",
						applicationScreen: ApplicationScreen.AdminTelehealthQueue,
						action: "Updated consult priority"
					}
				]);
			} catch (error) {
				console.log("Error creating audit log", error);
			}
		};

		const renderProperty = (label: string, value: string) => {
			return (
				<div className="felx flex-col">
					<div>{label}</div>
					<div className="ch-m-text">{value}</div>
				</div>
			);
		};

		return (
			<Dialog maxWidth={false} open={open}>
				<div
					className="flex flex-col items-start p-5"
					style={{ width: 640, borderRadius: 24 }}
				>
					{!isError && (
						<>
							<div className="flex justify-between w-full ch-l-text pb-6">
								<div>{`${label} Patient`}</div>
								<img
									src="images/svg/close.svg"
									width="20"
									height="20"
									alt="close modal"
									style={{ cursor: "pointer" }}
									onClick={() => reset(false)}
								/>
							</div>
							<div
								className="flex justify-between w-full p-6 ch-lightgrey-background"
								style={{ borderRadius: 8 }}
							>
								<div className="flex flex-col gap-8">
									{renderProperty(
										"Patient Name",
										`${consult?.firstName} ${consult?.lastName}`.trim()
									)}
									{renderProperty("Comment", `${consult?.comment || ""}`)}
								</div>
								<div className="flex flex-col gap-8">
									{renderProperty(
										"Time Waited",
										calculateWaitTime(consult?.arrivalDateTime)
									)}
								</div>
							</div>

							<div className="flex justify-between w-full mt-6">
								{!isLoading && (
									<>
										<button
											className="ch-button ch-button-primary justify-center"
											onClick={() => reset(false)}
										>
											Go Back
										</button>
										<button
											className="ch-button ch-button-secondary justify-center"
											onClick={() => handleConfirmClick()}
										>
											<img
												src={
													action === UpdatePriority.Prioritise
														? "images/svg/chevron-up-yellow.svg"
														: "images/svg/chevron-down-yellow.svg"
												}
												width="20"
												height="20"
												alt={`${label} patient`}
											/>
											{label} Patient
										</button>
									</>
								)}
								{isLoading && (
									<div className="flex justify-center py-3 w-full">
										<CircularProgress size="80px" />
									</div>
								)}
							</div>
						</>
					)}
					{isError && (
						<div className="flex flex-col items-center py-3">
							<img
								src="/images/svg/road-sign-warning.svg"
								height="50"
								alt="error icon"
							/>
							<p className="ch-m-text py-3 text-center">
								An error has occurred. The patient has not been{" "}
								{action === UpdatePriority.Prioritise
									? "prioirtised"
									: "de-prioritised"}
								. Try again later
							</p>
							<div className="flex gap-3">
								<button
									className="ch-button ch-button-primary"
									onClick={() => reset(false)}
								>
									Go back
								</button>
							</div>
						</div>
					)}
				</div>
			</Dialog>
		);
	}
);
